// RecipientPicker — choose email recipients by picking system users (from
// /api/v1/team) AND/OR typing arbitrary email addresses. Controlled: the parent
// owns the `value` string[] of emails and receives changes via `onChange`.
//
// Used by the Reports "Send now" flow and the scheduled-report add/edit forms so
// both offer the same user-picker + free-text experience.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X, Check } from 'lucide-react';
import { ticketingApi } from '@/lib/ticketing-api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function RecipientPicker({
  value, onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const { t } = useTranslation('components');
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // System users (customer org members / tenant users). Best-effort: if it fails
  // (e.g. no org yet), the free-text field still works.
  const { data: team = [], isLoading } = useQuery({
    queryKey: ['team'],
    queryFn: () => ticketingApi.listTeam(),
    staleTime: 60_000,
  });

  const has = (email: string) => value.some((v) => v.toLowerCase() === email.toLowerCase());
  const toggle = (email: string) => {
    if (!email) return;
    onChange(has(email) ? value.filter((v) => v.toLowerCase() !== email.toLowerCase()) : [...value, email]);
  };
  const addDraft = () => {
    const e = draft.trim();
    if (!e) return;
    if (!EMAIL_RE.test(e)) { setErr(t('recipientPicker.invalidEmail')); return; }
    if (!has(e)) onChange([...value, e]);
    setDraft(''); setErr(null);
  };

  // Users with a real email, shown as a checkbox list.
  const users = team.filter((m) => m.email && EMAIL_RE.test(m.email));
  // Free-typed emails not corresponding to a listed user (shown as removable chips).
  const extra = value.filter((v) => !users.some((u) => u.email.toLowerCase() === v.toLowerCase()));

  return (
    <div className="space-y-2">
      {/* System users */}
      <div className="max-h-40 overflow-y-auto rounded-lg border border-white/10 bg-black/20 p-1.5">
        {isLoading ? (
          <p className="px-2 py-1.5 text-xs text-muted">{t('recipientPicker.loadingUsers')}</p>
        ) : users.length === 0 ? (
          <p className="px-2 py-1.5 text-xs text-muted">{t('recipientPicker.noUsers')}</p>
        ) : (
          users.map((u) => {
            const checked = has(u.email);
            return (
              <button
                type="button" key={u.id} onClick={() => toggle(u.email)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start hover:bg-white/5"
              >
                <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${checked ? 'border-primary bg-primary text-white' : 'border-white/25'}`}>
                  {checked && <Check size={11} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{u.name || u.email}</span>
                  {u.name && <span className="block truncate text-xs text-muted">{u.email}</span>}
                </span>
              </button>
            );
          })
        )}
      </div>

      {/* Free-text email add */}
      <div className="flex gap-2">
        <input
          className="tk-input flex-1" type="email" inputMode="email" value={draft}
          placeholder={t('recipientPicker.addEmailPlaceholder')}
          onChange={(e) => { setDraft(e.target.value); setErr(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDraft(); } }}
        />
        <button type="button" className="tk-btn-ghost px-2" onClick={addDraft} aria-label={t('recipientPicker.addEmail')}><Plus size={16} /></button>
      </div>
      {err && <p className="text-xs text-critical">{err}</p>}

      {/* Chips for free-typed emails */}
      {extra.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {extra.map((e) => (
            <span key={e} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-xs">
              {e}
              <button type="button" onClick={() => toggle(e)} aria-label={t('recipientPicker.remove')}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}

      <p className="text-xs text-muted">{t('recipientPicker.selectedCount', { count: value.length })}</p>
    </div>
  );
}
